import { usePromisedRequest as promiseRequest } from "../Common/request";
import { BracketType } from "../Common/types";
import { FightResult } from "../Common/Fights/types";
import { resolveWithBody } from "./Utils/utils";

export type Opponent = {
    id: string;
    firstName: string;
    lastName: string;
    academyName: string;
    branch: string;
};

export type OverallFightRecord = {
    wins: number;
    losses: number;
};

export type FullFightRecord = {
    id: number;
    eventId: number;
    eventUrl: string;
    bracketUrl: string;
    eventFullName: string;
    finished: string;
    parameterId: number;
    categoryName: string;
    result: FightResult;
    bracketType: BracketType;
    opponent?: Opponent;
    winner?: boolean;
};

export type CompetitorOwnRecordProfile = {
    competitorId: number;
    firstName: string;
    lastName: string;
    nationality: string | null;
    academyName: string;
    academyId: number;
    branch: string;
    overallFightRecord: OverallFightRecord;
    fightRecords: FullFightRecord[];
    photoUrl: string | null;
};

export type CompetitorRecordProfile = CompetitorOwnRecordProfile & {
    id: string; //eventRegistrationId.
};

export type MethodBucketSlice = {
    key: string;
    count: number;
    percentage: number;
};

export type MethodRow = {
    key: string;
    translationKey: string;
    wins: number;
    losses: number;
};

export type RecordProfileStatistics = {
    winsTotal: number;
    lossesTotal: number;
    winSlices: MethodBucketSlice[];
    lossSlices: MethodBucketSlice[];
    methods: MethodRow[];
};

export default {
    getCompetitorRecordProfile(eventId: number, publicId: string): Promise<[CompetitorRecordProfile, 200] | [null, 401 | 403]> {
        return promiseRequest<CompetitorRecordProfile>({
            automaticallyRequireLogin: false,
            url: `/api/events/${eventId}/record-profiles/${publicId}`,
            method: "get",
            json: true,
        }).then(res => {
            if (res.statusCode === 401) {
                return [null, 401 as const];
            }
            if (res.statusCode === 403) {
                return [null, 403 as const];
            }
            if (res.statusCode !== 200) {
                throw new Error("Could not fetch profile.");
            }
            return [res.body, 200 as const];
        });
    },

    getFullFightRecords(publicId: string): Promise<FullFightRecord[]> {
        return promiseRequest<FullFightRecord[]>({
            url: `/api/record-profiles/${publicId}/fights`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    getCompetitorStatistics(publicId: string): Promise<RecordProfileStatistics> {
        return promiseRequest<RecordProfileStatistics>({
            url: `/api/record-profiles/${publicId}/statistics`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    getOwnStatistics(userId: number, competitorId: number): Promise<RecordProfileStatistics> {
        return promiseRequest<RecordProfileStatistics>({
            url: `/api/users/${userId}/competitors/${competitorId}/record-profile/statistics`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    getOwnRecordProfile(userId: number, competitorId: number): Promise<[CompetitorOwnRecordProfile, 200] | [null, 403]> {
        return promiseRequest<CompetitorOwnRecordProfile>({
            automaticallyRequireLogin: false,
            url: `/api/users/${userId}/competitors/${competitorId}/record-profile`,
            method: "get",
            json: true,
        }).then(res => {
            if (res.statusCode === 403) {
                return [null, 403 as const];
            }
            if (res.statusCode !== 200) {
                throw new Error("Could not fetch profile.");
            }
            return [res.body, 200 as const];
        });
    },

    getFullFightRecordsByCompetitorId(userId: number, competitorId: number): Promise<FullFightRecord[]> {
        return promiseRequest<FullFightRecord[]>({
            url: `/api/users/${userId}/competitors/${competitorId}/record-profile/fights`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};
